import { useEffect, useState } from "react";
import { Flame, ArrowRight, AlertTriangle, CalendarClock, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { fmtShiftTime, untilText } from "@/components/PresenceChips";

const SYNC_LABELS = {
  not_configured: "not configured — add the key under Settings → Platform → API Connections",
  unauthorized: "auth failed — key rejected (update it under Settings → Platform → API Connections)",
  token_expired: "API key expired — issue a new one and update it under Settings → Platform → API Connections",
  rate_limited: "rate limited",
  timeout: "timeout",
  http_error: "unreachable",
  unknown_error: "error",
};

const _ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

function Rotation() { return null; } // replaced by table layout below

/** Handover reminder switch + last sends (super admins only). */
function ReminderControls() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [log, setLog] = useState([]);
  const isSuper = (user?.role || "") === "super_admin";

  const load = () => api.get("/oncall/reminders")
    .then((r) => { setCfg(r.data.config); setLog(r.data.log || []); })
    .catch(() => setCfg(null));
  useEffect(() => { if (isSuper) load(); }, [isSuper]);

  if (!isSuper || !cfg) return null;

  const save = async (enabled) => {
    try {
      const r = await api.put("/oncall/reminders", { enabled });
      setCfg(r.data);
      toast.success(enabled ? "Handover reminders on" : "Handover reminders off");
    } catch { toast.error("Couldn't save that"); }
  };

  const runNow = async () => {
    try {
      const r = await api.post("/oncall/reminders/run");
      toast.success(r.data.sent ? `Sent ${r.data.sent} reminder(s)` : `Nothing due (${r.data.status})`);
      load();
    } catch { toast.error("Run failed"); }
  };

  const last = log[0];
  return (
    <div className="col-span-full flex flex-wrap items-center gap-3 border-t border-border px-3 py-2"
      data-testid="reminder-controls">
      <div className="flex items-center gap-2">
        <Switch checked={!!cfg.enabled} onCheckedChange={save} data-testid="reminder-enabled-switch" />
        <span className="text-xs font-medium text-foreground">Handover reminders</span>
      </div>
      <span className="text-[11px] text-muted-foreground">
        Emails the incoming and outgoing person {(cfg.lead_hours || []).join("h and ")}h before a shift change
      </span>
      <Button variant="outline" size="sm" className="ml-auto h-7 text-xs" onClick={runNow} data-testid="reminder-run-now">
        Check now
      </Button>
      {last && (
        <span className="w-full text-[11px] text-muted-foreground" data-testid="reminder-last">
          Last: {last.incoming} · {last.schedule} · {last.lead_hours}h notice · {last.status}
          {last.status === "paused" && " (outbound email is paused platform-wide)"}
        </span>
      )}
    </div>
  );
}

/** Who's on call now, who's next and when the shift changes (incident.io rota). */
export const OnCallRota = ({ rota = [], syncError, compact = false }) => {
  const [open, setOpen] = useState(() => localStorage.getItem("rota_open") === "1");
  const toggle = () => setOpen((o) => { localStorage.setItem("rota_open", o ? "0" : "1"); return !o; });

  if (syncError) {
    return (
      <div data-testid="rota-sync-error"
        className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        On-call rota unavailable — incident.io {SYNC_LABELS[syncError] || syncError}
      </div>
    );
  }
  if (!rota.length) return null;

  const rotations = rota.flatMap((s) => s.rotations);
  const soonest = rotations
    .map((r) => r.current?.ends_at)
    .filter(Boolean)
    .sort((a, b) => new Date(a) - new Date(b))[0];

  return (
    <div data-testid="oncall-rota" className="rounded-xl border border-border bg-card shadow-sm">
      <button type="button" onClick={toggle} data-testid="oncall-rota-toggle"
        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/50">
        <CalendarClock className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold text-foreground">On call &amp; handover</span>
        <span className="text-[11px] text-muted-foreground">via incident.io</span>
        <span className="ml-auto flex items-center gap-2 text-[11px] text-muted-foreground">
          <span className="hidden sm:inline">
            {rotations.length} rotation{rotations.length === 1 ? "" : "s"}
            {soonest ? ` · next handover ${untilText(soonest)}` : ""}
          </span>
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {open && (
        <div className="border-t border-border" data-testid="oncall-rota-body">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border/60 text-left text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-bold">Schedule</th>
                  <th className="px-3 py-2 font-bold">On call now</th>
                  <th className="px-3 py-2 font-bold">Until handover</th>
                  <th className="px-3 py-2 font-bold">Up next</th>
                </tr>
              </thead>
              <tbody>
                {rota.map((s) => s.rotations.map((rot, i) => {
                  const cur = rot.current, nxt = rot.next;
                  const multi = s.rotations.length > 1;
                  return (
                    <tr key={`${s.id}-${rot.rotation_id}`} data-testid={`rota-rotation-${s.id}-${rot.rotation_id}`}
                      className="border-b border-border/40 last:border-b-0 hover:bg-muted/30">
                      <td className="px-3 py-2 align-top">
                        <div className="flex items-center gap-1.5">
                          {multi && (
                            <span data-testid={`rota-escalation-${s.id}-${rot.rotation_id}`} title={`Escalation level ${i + 1}`}
                              className="rounded-full bg-slate-200/80 px-1.5 py-0.5 text-[9px] font-bold text-slate-600 dark:bg-slate-700 dark:text-slate-300">{_ordinal(i + 1)}</span>
                          )}
                          <span className="font-semibold text-foreground">{s.name}</span>
                        </div>
                        {s.timezone && <div className="text-[10px] text-muted-foreground/70">{s.timezone}</div>}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span className="inline-flex items-center gap-1 font-semibold text-orange-700 dark:text-orange-300">
                          <Flame className="h-3 w-3" fill="currentColor" />{cur?.name || "Nobody"}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top text-muted-foreground">
                        {cur?.ends_at
                          ? <span>{fmtShiftTime(cur.ends_at)} <span className="font-semibold text-foreground/70">({untilText(cur.ends_at)})</span></span>
                          : "—"}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {nxt?.name ? (
                          <div>
                            <span className="font-medium text-sky-700 dark:text-sky-300">{nxt.name}</span>
                            {nxt.starts_at && <div className="text-[10px] text-muted-foreground">takes over {fmtShiftTime(nxt.starts_at)}</div>}
                          </div>
                        ) : <span className="text-muted-foreground">no one scheduled</span>}
                      </td>
                    </tr>
                  );
                }))}
              </tbody>
            </table>
          </div>
          <ReminderControls />
        </div>
      )}
    </div>
  );
};
